import { useParams, Link } from "@/lib/router-compat";
import { jsonLd } from "@/lib/seoHelpers";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { supabase } from "@/integrations/supabase/client";
import { reviewSections } from "@/lib/reviewSectionConfig";
import { brandConfig, getBrandLogoUrl } from "@/lib/brandConfig";
import { BreadcrumbSchema } from "@/components/SEO/BreadcrumbSchema";
import { categoryConfig } from "@/lib/categoryConfig";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, ChevronRight, ArrowRight } from "lucide-react";
import { InFeedAd } from "@/components/AdSense";
import { cn } from "@/lib/utils";
import NotFound from "./NotFound";

const BASE_URL = "https://www.garden-review.co.uk";

const ReviewSection = () => {
  const { sectionSlug } = useParams();
  const config = sectionSlug ? reviewSections[sectionSlug] : null;
  const [showAllSubcategories, setShowAllSubcategories] = useState(false);

  const { data: articles = [], isLoading: articlesLoading } = useQuery({
    queryKey: ["review-section-articles", sectionSlug],
    queryFn: async () => {
      if (!config || config.subcategories.length === 0) return [];
      const { data, error } = await supabase
        .from("articles")
        .select("id, title, slug, excerpt, featured_image, featured_image_alt, categories, published_at")
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data || []).filter(article =>
        article.categories?.some((cat: string) =>
          config.subcategories.some(sub =>
            cat.toLowerCase().includes(sub.replace(/-/g, " ")) ||
            cat.toLowerCase().includes(sub)
          )
        )
      );
    },
    enabled: !!config && config.subcategories.length > 0,
  });

  const { data: topLists = [], isLoading: topListsLoading } = useQuery({
    queryKey: ["review-section-toplists", sectionSlug],
    queryFn: async () => {
      if (!config) return [];
      const { data, error } = await supabase
        .from("top_lists")
        .select("id, title, slug, featured_image, featured_image_alt, meta_description, product_type")
        .eq("status", "published")
        .order("published_at", { ascending: false });
      if (error) throw error;

      // Only show guides that actually belong to this review section
      const subs = config.subcategories.map(s => s.toLowerCase());
      const subPhrases = subs.map(s => s.replace(/-/g, " "));
      return (data || []).filter(list => {
        const type = (list.product_type || "").toLowerCase().replace(/_/g, "-");
        if (type && subs.includes(type)) return true;
        const title = (list.title || "").toLowerCase();
        return subPhrases.some(phrase => {
          if (phrase.length < 4) return false;
          // match plural or singular form of the subcategory phrase
          const singular = phrase.replace(/ies$/, "y").replace(/s$/, "");
          return title.includes(phrase) || title.includes(singular);
        });
      });
    },
    enabled: !!config && !config.hideTopLists && config.subcategories.length > 0,
  });


  if (!config) return <NotFound />;

  const isLoading = articlesLoading || topListsLoading;
  const isBrandsPage = sectionSlug === "brands";

  const breadcrumbs = [
    { name: "Home", url: BASE_URL },
    { name: "Reviews", url: `${BASE_URL}/category/reviews` },
    { name: config.title, url: `${BASE_URL}/reviews/${config.slug}` },
  ];

  // Get the matching section from reviews config for subcategory links
  const reviewsConfig = categoryConfig.reviews;
  const matchingSection = reviewsConfig?.sections.find(
    s => s.title.toLowerCase().replace(/\s+/g, "-") === sectionSlug ||
         (sectionSlug === "brands" && s.title === "By Brand") ||
         (sectionSlug === "power-tools" && s.title === "Power Tools") ||
         (sectionSlug === "garden-machinery" && s.title === "Garden Machinery") ||
         (sectionSlug === "hand-tools" && s.title === "Hand Tools") ||
         (sectionSlug === "garden-kit" && s.title === "Garden Kit") ||
         (sectionSlug === "outdoor-furniture" && s.title === "Outdoor Furniture")
  );

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: config.title,
    description: config.metaDescription,
    url: `${BASE_URL}/reviews/${config.slug}`,
    isPartOf: {
      "@type": "WebSite",
      name: "Garden Review",
      url: BASE_URL,
    },
  };

  return (
    <>
      <Helmet>
        <title>{config.metaTitle}</title>
        <meta name="description" content={config.metaDescription} />
        <link rel="canonical" href={`${BASE_URL}/reviews/${config.slug}`} />
        <meta property="og:title" content={config.metaTitle} />
        <meta property="og:description" content={config.metaDescription} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{jsonLd(pageSchema)}</script>
      </Helmet>
      <BreadcrumbSchema items={breadcrumbs} />

      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero — skinny single-row banner */}
        <section className={cn("relative overflow-hidden py-2 md:py-2.5", config.heroGradient)}>
          <div className="relative z-10 container mx-auto px-4 lg:px-8 max-w-5xl">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-1.5 md:gap-4 py-1">
              {/* Breadcrumb (left) */}
              <nav className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                <Link to="/" className="hover:text-foreground transition-colors whitespace-nowrap">Home</Link>
                <ChevronRight className="h-3 w-3 shrink-0" />
                <Link to="/category/reviews" className="hover:text-foreground transition-colors whitespace-nowrap">Reviews</Link>
                <ChevronRight className="h-3 w-3 shrink-0" />
                <span className="text-foreground font-medium whitespace-nowrap">{config.title}</span>
              </nav>
              {/* Title + description (right, inline with breadcrumb) */}
              <div className="flex items-center gap-2.5 md:justify-end min-w-0">
                <h1 className="text-lg md:text-xl font-bold text-foreground whitespace-nowrap leading-tight">
                  {isBrandsPage ? config.title : `${config.title} Reviews`}
                </h1>
                <span className="hidden md:block text-foreground/30" aria-hidden="true">·</span>
                <p className="hidden md:block text-sm text-foreground/75 line-clamp-1 max-w-md">
                  {config.description}
                </p>
              </div>
            </div>
          </div>
        </section>

        <main className="container mx-auto px-4 py-10 max-w-5xl">
          {/* Subcategory nav badges */}
          {matchingSection && (() => {
            const SUBCATEGORY_LIMIT = 12;
            const totalLinks = matchingSection.links.length;
            const hasOverflow = totalLinks > SUBCATEGORY_LIMIT;
            const visibleLinks = showAllSubcategories || !hasOverflow
              ? matchingSection.links
              : matchingSection.links.slice(0, SUBCATEGORY_LIMIT);

            return (
              <section className="mb-10">
                <h2 className="text-xl font-semibold text-foreground mb-4">
                  Browse {config.title}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {visibleLinks.map(link => (
                    <Link
                      key={link.slug}
                      to={
                        link.href
                          ? link.href
                          : sectionSlug === "brands"
                            ? `/brand/${link.slug}`
                            : `/category/reviews?sub=${link.slug}`
                      }
                    >
                      <Badge variant="secondary" className="px-4 py-2 text-sm hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer">
                        {link.name}
                      </Badge>
                    </Link>
                  ))}
                  {hasOverflow && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAllSubcategories(prev => !prev)}
                      className="h-auto px-4 py-2 text-sm"
                      aria-expanded={showAllSubcategories}
                    >
                      {showAllSubcategories
                        ? "Show fewer"
                        : `Show all ${totalLinks} categories`}
                      <ChevronDown
                        className={cn(
                          "ml-1 h-3.5 w-3.5 transition-transform",
                          showAllSubcategories && "rotate-180"
                        )}
                      />
                    </Button>
                  )}
                </div>
              </section>
            );
          })()}

          {/* Brands grid for the brands page */}
          {isBrandsPage && (
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6">All Brands</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.values(brandConfig).map(brand => {
                  const logoUrl = getBrandLogoUrl(brand.slug);
                  return (
                    <Link key={brand.slug} to={`/brand/${brand.slug}`}>
                      <Card className="hover:shadow-md transition-shadow h-full">
                        <CardContent className="p-5">
                          <div className="flex items-center gap-3 mb-2">
                            {logoUrl && (
                              <img
                                src={logoUrl}
                                alt={`${brand.name} logo`}
                                className="h-10 w-10 object-contain rounded bg-white border p-1 shrink-0"
                                loading="lazy"
                                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                            <h3 className="text-lg font-semibold text-foreground">{brand.name}</h3>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{brand.description}</p>
                          <span className="inline-flex items-center text-primary text-sm mt-3 font-medium">
                            View Reviews <ArrowRight className="h-4 w-4 ml-1" />
                          </span>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Loading state */}
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-64 rounded-lg" />
              ))}
            </div>
          )}

          {/* Top Lists / Best-Of Guides */}
          {topLists.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                Best-Of Guides
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {topLists.map(list => (
                  <Link key={list.id} to={`/best/${list.slug}`}>
                    <Card className="overflow-hidden hover:shadow-md transition-shadow h-full">
                      {list.featured_image && (
                        <img
                          src={list.featured_image}
                          alt={list.featured_image_alt || list.title}
                          className="w-full h-40 object-cover"
                          loading="lazy"
                        />
                      )}
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-foreground line-clamp-2">{list.title}</h3>
                        {list.meta_description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{list.meta_description}</p>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* In-feed ad between Best-Of Guides and Articles */}
          {(topLists.length > 0 || articles.length > 0) && (
            <div className="my-4">
              <InFeedAd />
            </div>
          )}

          {/* Articles */}
          {articles.length > 0 && (
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                {config.title} Articles & Guides
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map(article => (
                  <Link key={article.id} to={`/articles/${article.slug}`}>
                    <Card className="overflow-hidden hover:shadow-md transition-shadow h-full">
                      {article.featured_image && (
                        <img
                          src={article.featured_image}
                          alt={article.featured_image_alt || article.title}
                          className="w-full h-40 object-cover"
                          loading="lazy"
                        />
                      )}
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-foreground line-clamp-2">{article.title}</h3>
                        {article.excerpt && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{article.excerpt}</p>
                        )}
                        {article.published_at && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {new Date(article.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Empty state */}
          {!isLoading && articles.length === 0 && topLists.length === 0 && !isBrandsPage && (
            <div className="text-center py-16">
              <p className="text-muted-foreground text-lg">
                No reviews available in this section yet. Check back soon!
              </p>
              <Link to="/category/reviews" className="text-primary hover:underline mt-4 inline-block">
                Browse all reviews
              </Link>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ReviewSection;
